import {
  revitBridge,
  type ExpectedRevitDocumentIdentity,
} from '../../api/revit/revitBridge';
import type { RevitOperationProgressSnapshot } from '../../api/grpc/operationProgressClient';
import {
  createLoadedBoltFamilyReplacementPlan,
  getBoltFamilyVerificationParameterNames,
  BOLT_REFERENCE_FAMILY_FILES,
  type BoltDefaultParameterRemoval,
  type BoltFamilyParameterBindingChange,
  type BoltModelingType,
  type LoadedBoltFamilyReplacementPlan,
} from './planner';
import {
  addParametersToBoltFamilies,
  rebindBoltFamilyParameters,
  removeBoltFamilyParameters,
} from './familyParameterHostActions';
import type { BoltParameterTypeHint } from './boltParameterTypes';
import {
  invalidateFamilySnapshotCache,
  refreshFamilySnapshotResult,
} from '../../lib/revit/familySnapshotCache';
import { ensureLoadedFamilyDiscriminator } from './loadedFamilyDiscriminator';

const LoadedFamilyReadAttempts = 8;
const LoadedFamilyReadRetryDelayMs = process.env.NODE_ENV === 'test' ? 0 : 250;

export interface LoadedBoltFamilyReconciliationResult {
  success: boolean;
  familyName?: string;
  familyId?: string;
  addedFamilyParameterNames?: string[];
  reboundFamilyParameters?: BoltFamilyParameterBindingChange[];
  removedDefaultParameters?: BoltDefaultParameterRemoval[];
  undoAvailable?: boolean;
  error?: string;
}

async function readLoadedFamilyReplacementPlan(input: {
  targetObjectType: BoltModelingType;
  loadedFamilyId?: string;
  loadedFamilyName?: string;
  parameterNames: string[];
  bindingKind: 'type' | 'instance';
  objectTypeParameterName: string;
  cacheScopeKey?: string | null;
}): Promise<{ success: boolean; plan?: LoadedBoltFamilyReplacementPlan; error?: string }> {
  const verificationParameterNames = getBoltFamilyVerificationParameterNames(
    input.parameterNames,
    input.objectTypeParameterName
  );
  const snapshot = await refreshFamilySnapshotResult({
    cacheScopeKey: input.cacheScopeKey,
    parameterNames: verificationParameterNames,
    resolveScopedFamilyIds: (families) => {
      const broadPlan = createLoadedBoltFamilyReplacementPlan({
        families,
        loadedFamilyId: input.loadedFamilyId,
        loadedFamilyName: input.loadedFamilyName,
        targetObjectType: input.targetObjectType,
        objectTypeParameterName: input.objectTypeParameterName,
        bindingKind: input.bindingKind,
        selectedParameterNames: input.parameterNames,
      });
      const scopedFamilyId = broadPlan.loadedFamily?.id || input.loadedFamilyId;
      return scopedFamilyId ? [scopedFamilyId] : [];
    },
  });
  if (!snapshot.available) {
    return {
      success: false,
      error:
        snapshot.error ||
        `Failed to read loaded ${input.targetObjectType} family parameters`,
    };
  }

  const plan = createLoadedBoltFamilyReplacementPlan({
    families: snapshot.families,
    loadedFamilyId: input.loadedFamilyId,
    loadedFamilyName: input.loadedFamilyName,
    targetObjectType: input.targetObjectType,
    objectTypeParameterName: input.objectTypeParameterName,
    bindingKind: input.bindingKind,
    selectedParameterNames: input.parameterNames,
  });

  return { success: true, plan };
}

function delay(ms: number): Promise<void> {
  return ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve();
}

async function waitForLoadedFamilyReplacementPlan(
  input: {
    targetObjectType: BoltModelingType;
    loadedFamilyId?: string;
    loadedFamilyName?: string;
    parameterNames: string[];
    bindingKind: 'type' | 'instance';
    objectTypeParameterName: string;
    cacheScopeKey?: string | null;
  },
  isReady: (plan: LoadedBoltFamilyReplacementPlan) => boolean
): Promise<{ success: boolean; plan?: LoadedBoltFamilyReplacementPlan; error?: string }> {
  let latestResult: { success: boolean; plan?: LoadedBoltFamilyReplacementPlan; error?: string } | null = null;
  let latestPlanResult: { success: boolean; plan?: LoadedBoltFamilyReplacementPlan; error?: string } | null = null;

  for (let attempt = 0; attempt < LoadedFamilyReadAttempts; attempt++) {
    latestResult = await readLoadedFamilyReplacementPlan(input);
    if (latestResult.success && latestResult.plan) {
      latestPlanResult = latestResult;
    }

    if (latestResult.success && latestResult.plan && isReady(latestResult.plan)) {
      return latestResult;
    }

    if (attempt < LoadedFamilyReadAttempts - 1) {
      await delay(LoadedFamilyReadRetryDelayMs);
    }
  }

  if (latestPlanResult) {
    return latestPlanResult;
  }

  return latestResult ?? {
    success: false,
    error: `Failed to read loaded ${input.targetObjectType} family parameters`,
  };
}

function describeUnverifiedLoadedFamilyPlan(
  plan: LoadedBoltFamilyReplacementPlan,
  targetObjectType: BoltModelingType
): string {
  const reasons: string[] = [];
  if (!plan.loadedFamily) {
    reasons.push('loaded family was not returned by the host');
  } else if (!plan.loadedFamilyMatchesObjectType) {
    reasons.push(`loaded family does not expose OBJECT-TYPE = ${targetObjectType}`);
  }

  if (plan.selectedNamesToAdd.length > 0) {
    reasons.push(`missing selected parameters: ${plan.selectedNamesToAdd.join(', ')}`);
  }

  if (plan.selectedParametersToRebind.length > 0) {
    reasons.push(
      `binding mismatch: ${plan.selectedParametersToRebind
        .map(
          (parameter) =>
            `${parameter.parameterName} (${parameter.previousBindingKind} -> ${parameter.bindingKind})`
        )
        .join(', ')}`
    );
  }

  if (plan.defaultParametersToRemove.length > 0) {
    reasons.push(
      `unselected default parameters still present: ${plan.defaultParametersToRemove
        .map((parameter) => parameter.parameterName)
        .join(', ')}`
    );
  }

  return reasons.length > 0
    ? `Could not verify loaded ${targetObjectType} family: ${reasons.join('; ')}`
    : `Could not verify loaded ${targetObjectType} family`;
}

export async function loadReferenceFamilyAndReconcileParameters(input: {
  targetObjectType: BoltModelingType;
  parameterNames: string[];
  bindingKind: 'type' | 'instance';
  objectTypeParameterName: string;
  objectTypeBindingKind: 'type' | 'instance';
  reconcileFamilyParameters?: boolean;
  parameterTypeHints?: BoltParameterTypeHint[];
  expectedDocumentIdentity?: ExpectedRevitDocumentIdentity;
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}): Promise<LoadedBoltFamilyReconciliationResult> {
  const cacheScopeKey =
    input.expectedDocumentIdentity?.expectedDocumentPath ??
    input.expectedDocumentIdentity?.expectedDocumentName ??
    null;
  const refFamilyFileName = BOLT_REFERENCE_FAMILY_FILES[input.targetObjectType];
  const loadResult = await revitBridge.loadFamilyIntoProject(refFamilyFileName, {
    onProgress: input.onProgress,
  });

  if (!loadResult.success || !loadResult.data) {
    return {
      success: false,
      error: loadResult.error || `Failed to load reference family: ${refFamilyFileName}`,
    };
  }
  invalidateFamilySnapshotCache(cacheScopeKey);

  const initialPlanResult = await waitForLoadedFamilyReplacementPlan({
    targetObjectType: input.targetObjectType,
    loadedFamilyId: loadResult.data.familyId,
    loadedFamilyName: loadResult.data.familyName,
    parameterNames: input.parameterNames,
    bindingKind: input.bindingKind,
    objectTypeParameterName: input.objectTypeParameterName,
    cacheScopeKey,
  }, (plan) => Boolean(plan.loadedFamily && plan.loadedFamilyMatchesObjectType));
  if (!initialPlanResult.success || !initialPlanResult.plan) {
    return {
      success: false,
      familyName: loadResult.data.familyName,
      familyId: loadResult.data.familyId,
      error:
        initialPlanResult.error || `Could not inspect loaded ${input.targetObjectType} family`,
    };
  }

  let initialPlan = initialPlanResult.plan;
  if (initialPlan.loadedFamily && !initialPlan.loadedFamilyMatchesObjectType) {
    // The shipped reference may predate this project's discriminator.
    const objectTypeRepair = await ensureLoadedFamilyDiscriminator({
      plan: initialPlan,
      targetObjectType: input.targetObjectType,
      objectTypeParameterName: input.objectTypeParameterName,
      objectTypeBindingKind: input.objectTypeBindingKind,
      expectedDocumentIdentity: input.expectedDocumentIdentity,
      cacheScopeKey,
      onProgress: input.onProgress,
    });
    if (!objectTypeRepair.success) {
      return {
        success: false,
        familyName: loadResult.data.familyName,
        familyId: loadResult.data.familyId,
        error: objectTypeRepair.error,
      };
    }

    const repairedPlanResult = await waitForLoadedFamilyReplacementPlan({
      targetObjectType: input.targetObjectType,
      loadedFamilyId: loadResult.data.familyId,
      loadedFamilyName: loadResult.data.familyName,
      parameterNames: input.parameterNames,
      bindingKind: input.bindingKind,
      objectTypeParameterName: input.objectTypeParameterName,
      cacheScopeKey,
    }, (plan) => Boolean(plan.loadedFamily && plan.loadedFamilyMatchesObjectType));
    if (repairedPlanResult.plan) {
      initialPlan = repairedPlanResult.plan;
    }
  }

  if (!initialPlan.loadedFamily || !initialPlan.loadedFamilyMatchesObjectType) {
    return {
      success: false,
      familyName: loadResult.data.familyName,
      familyId: loadResult.data.familyId,
      error: describeUnverifiedLoadedFamilyPlan(initialPlan, input.targetObjectType),
    };
  }

  if (input.reconcileFamilyParameters === false) {
    return {
      success: true,
      familyName: initialPlan.loadedFamily.name || loadResult.data.familyName || refFamilyFileName,
      familyId: initialPlan.loadedFamily.id || loadResult.data.familyId,
      undoAvailable: false,
    };
  }

  // Family-document edits reload the definition and can replace the Revit
  // Family element. Prefer the identity observed after reconciliation instead
  // of continuing with the ID returned by the original load operation.
  let loadedFamilyId = initialPlan.loadedFamily.id || loadResult.data.familyId;
  let loadedFamilyName = initialPlan.loadedFamily.name || loadResult.data.familyName;

  const refreshLoadedFamilyIdentity = async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    const refreshedPlanResult = await waitForLoadedFamilyReplacementPlan({
      targetObjectType: input.targetObjectType,
      loadedFamilyId,
      loadedFamilyName,
      parameterNames: input.parameterNames,
      bindingKind: input.bindingKind,
      objectTypeParameterName: input.objectTypeParameterName,
      cacheScopeKey,
    }, (plan) => Boolean(plan.loadedFamily && plan.loadedFamilyMatchesObjectType));
    const refreshedPlan = refreshedPlanResult.plan;
    const refreshedFamily = refreshedPlan?.loadedFamily;
    if (
      !refreshedPlanResult.success ||
      !refreshedFamily ||
      !refreshedPlan.loadedFamilyMatchesObjectType
    ) {
      return {
        success: false,
        error:
          refreshedPlanResult.error ||
          (refreshedPlan
            ? describeUnverifiedLoadedFamilyPlan(refreshedPlan, input.targetObjectType)
            : `Could not refresh loaded ${input.targetObjectType} family after parameter changes`),
      };
    }

    loadedFamilyId = refreshedFamily.id || loadedFamilyId;
    loadedFamilyName = refreshedFamily.name || loadedFamilyName;
    return { success: true };
  };

  if (initialPlan.selectedNamesToAdd.length > 0) {
    const addResult = await addParametersToBoltFamilies({
      familyIds: [loadedFamilyId],
      targetObjectType: input.targetObjectType,
      parameterNames: initialPlan.selectedNamesToAdd,
      bindingKind: input.bindingKind,
      parameterTypeHints: input.parameterTypeHints,
      options: {
        expectedDocumentIdentity: input.expectedDocumentIdentity,
        onProgress: input.onProgress,
      },
    });
    invalidateFamilySnapshotCache(cacheScopeKey);

    if (!addResult.success) {
      return {
        success: false,
        familyName: loadResult.data.familyName,
        familyId: loadResult.data.familyId,
        error:
          addResult.error ||
          `Failed to add selected ${input.targetObjectType} family parameters`,
      };
    }

    const refreshedIdentity = await refreshLoadedFamilyIdentity();
    if (!refreshedIdentity.success) {
      return {
        success: false,
        familyName: loadedFamilyName,
        familyId: loadedFamilyId,
        error: refreshedIdentity.error,
      };
    }
  }

  const rebindResult = await rebindBoltFamilyParameters({
    familyId: loadedFamilyId,
    familyName: loadedFamilyName,
    parametersToRebind: initialPlan.selectedParametersToRebind,
    options: {
      expectedDocumentIdentity: input.expectedDocumentIdentity,
    },
  });
  if (initialPlan.selectedParametersToRebind.length > 0) {
    invalidateFamilySnapshotCache(cacheScopeKey);
  }
  if (!rebindResult.success) {
    return {
      success: false,
      familyName: loadResult.data.familyName,
      familyId: loadResult.data.familyId,
      error:
        rebindResult.error ||
        `Failed to update selected ${input.targetObjectType} family parameter bindings`,
    };
  }
  if (initialPlan.selectedParametersToRebind.length > 0) {
    const refreshedIdentity = await refreshLoadedFamilyIdentity();
    if (!refreshedIdentity.success) {
      return {
        success: false,
        familyName: loadedFamilyName,
        familyId: loadedFamilyId,
        error: refreshedIdentity.error,
      };
    }
  }

  const removeResult = await removeBoltFamilyParameters({
    familyId: loadedFamilyId,
    familyName: loadedFamilyName,
    parametersToRemove: initialPlan.defaultParametersToRemove,
    options: {
      expectedDocumentIdentity: input.expectedDocumentIdentity,
    },
  });
  if (initialPlan.defaultParametersToRemove.length > 0) {
    invalidateFamilySnapshotCache(cacheScopeKey);
  }
  if (!removeResult.success) {
    return {
      success: false,
      familyName: loadResult.data.familyName,
      familyId: loadResult.data.familyId,
      error:
        removeResult.error ||
        `Failed to remove unselected default ${input.targetObjectType} family parameters`,
    };
  }

  const mutated =
    initialPlan.selectedNamesToAdd.length > 0 ||
    initialPlan.selectedParametersToRebind.length > 0 ||
    initialPlan.defaultParametersToRemove.length > 0;
  const finalPlanResult = mutated
    ? await waitForLoadedFamilyReplacementPlan({
        targetObjectType: input.targetObjectType,
        loadedFamilyId,
        loadedFamilyName,
        parameterNames: input.parameterNames,
        bindingKind: input.bindingKind,
        objectTypeParameterName: input.objectTypeParameterName,
        cacheScopeKey,
      }, (plan) => plan.isVerified)
    : { success: true, plan: initialPlan };
  const finalPlan = finalPlanResult.plan;
  if (!finalPlanResult.success || !finalPlan || !finalPlan.isVerified) {
    // Snapshot type rows structurally cannot show instance-bound family
    // parameters of an unplaced family. The add above committed them on the
    // host, so names confirmed added in this run count as satisfied; the
    // OBJECT-TYPE check still requires the snapshot to agree.
    const additionsConfirmedByHost =
      input.bindingKind === 'instance' &&
      initialPlan.selectedNamesToAdd.length > 0 &&
      Boolean(finalPlan?.loadedFamilyMatchesObjectType) &&
      (finalPlan?.selectedNamesToAdd ?? []).every((name) =>
        initialPlan.selectedNamesToAdd.includes(name)
      );

    if (!additionsConfirmedByHost) {
      return {
        success: false,
        familyName: loadedFamilyName,
        familyId: loadedFamilyId,
        error:
          finalPlanResult.error ||
          (finalPlan
            ? describeUnverifiedLoadedFamilyPlan(finalPlan, input.targetObjectType)
            : `Could not verify loaded ${input.targetObjectType} family`),
      };
    }
  }

  if (finalPlan?.loadedFamily) {
    loadedFamilyId = finalPlan.loadedFamily.id || loadedFamilyId;
    loadedFamilyName = finalPlan.loadedFamily.name || loadedFamilyName;
  }

  return {
    success: true,
    familyName: loadedFamilyName,
    familyId: loadedFamilyId,
    addedFamilyParameterNames: initialPlan.selectedNamesToAdd,
    reboundFamilyParameters: initialPlan.selectedParametersToRebind,
    removedDefaultParameters: initialPlan.defaultParametersToRemove,
    undoAvailable: mutated,
  };
}
